import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BarChart3,
  Bell,
  Boxes,
  Bug,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  FilePlus,
  Files,
  Home,
  Link2,
  LogOut,
  Menu,
  Package,
  ReceiptText,
  Scale,
  Settings,
  ShoppingCart,
  Users,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { ModuleId } from '../api/client';
import { useModules } from '../context/ModulesContext';
import { getSettings, trackUserUsagePageView } from '../api/client';
import { featureForPath } from '../lib/userUsage';
import LanguageSwitcher from './LanguageSwitcher';
import WorkspaceSwitcher from './WorkspaceSwitcher';

type NavItem = {
  to: string;
  icon: typeof Home;
  labelKey: string;
  moduleId?: ModuleId;
  requiresPaymentLinks?: boolean;
};

type NavGroup = {
  labelKey?: string;
  items: NavItem[];
};

let lastTrackedUsageTransition: string | null = null;
const SIDEBAR_COLLAPSED_STORAGE_KEY = 'dada_sidebar_collapsed';

const navGroups: NavGroup[] = [
  {
    items: [
      { to: '/home', icon: Home, labelKey: 'nav.home' },
    ],
  },
  {
    labelKey: 'navGroups.create',
    items: [
      { to: '/create-document', icon: FilePlus, labelKey: 'nav.createDocument' },
      { to: '/create-payment', icon: CreditCard, labelKey: 'nav.createPayment', requiresPaymentLinks: true },
    ],
  },
  {
    labelKey: 'navGroups.money',
    items: [
      { to: '/documents', icon: Files, labelKey: 'nav.documents' },
      { to: '/collections', icon: ReceiptText, labelKey: 'nav.collections', moduleId: 'collections' },
      { to: '/payment-links', icon: Link2, labelKey: 'nav.paymentLinks', moduleId: 'collections' },
      { to: '/reconciliation', icon: Scale, labelKey: 'nav.reconciliation' },
      { to: '/reports', icon: BarChart3, labelKey: 'nav.reports', moduleId: 'analytics' },
    ],
  },
  {
    labelKey: 'navGroups.customersItems',
    items: [
      { to: '/customers', icon: Users, labelKey: 'nav.customers' },
      { to: '/reminders', icon: Bell, labelKey: 'nav.reminders' },
      { to: '/items', icon: Package, labelKey: 'nav.items' },
      { to: '/orders', icon: ShoppingCart, labelKey: 'nav.orders', moduleId: 'orders' },
    ],
  },
  {
    labelKey: 'navGroups.automation',
    items: [
      { to: '/automations', icon: Boxes, labelKey: 'nav.automations' },
      { to: '/notifications', icon: Bell, labelKey: 'nav.notifications' },
      { to: '/bugs', icon: Bug, labelKey: 'nav.bugs' },
    ],
  },
];

const settingsNavItem: NavItem = { to: '/settings', icon: Settings, labelKey: 'nav.settings' };

export default function Layout() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { isModuleEnabled, loading: modulesLoading } = useModules();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paymentLinksEnabled, setPaymentLinksEnabled] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(collapsed));
    } catch {
      // The sidebar remains usable when storage is unavailable or blocked.
    }
  }, [collapsed]);

  useEffect(() => {
    let active = true;
    setSettingsLoaded(false);
    setPaymentLinksEnabled(false);
    getSettings()
      .then((res) => {
        const data = res.data?.settings ?? res.data ?? {};
        if (active) setPaymentLinksEnabled(data.payment_links_enabled === true);
      })
      .catch(() => {
        if (active) setPaymentLinksEnabled(false);
      })
      .finally(() => {
        if (active) setSettingsLoaded(true);
      });
    return () => {
      active = false;
    };
  }, [user?.tenant_id]);

  useEffect(() => {
    if (!user?.tenant_id) return;
    const transitionKey = `${user.tenant_id}:${location.pathname}`;
    if (lastTrackedUsageTransition === transitionKey) return;
    lastTrackedUsageTransition = transitionKey;
    void trackUserUsagePageView(featureForPath(location.pathname)).catch((error) => {
      console.warn('Failed to record page view', error);
    });
  }, [location.pathname, user?.tenant_id]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail ?? {};
      setPaymentLinksEnabled(detail.payment_links_enabled === true);
    };
    window.addEventListener('settings-changed', handler);
    return () => window.removeEventListener('settings-changed', handler);
  }, []);

  const navReady = settingsLoaded && !modulesLoading;

  const isItemVisible = useMemo(
    () => (item: NavItem) => {
      if (item.requiresPaymentLinks && !paymentLinksEnabled) return false;
      if (item.moduleId) return isModuleEnabled(item.moduleId);
      return true;
    },
    [paymentLinksEnabled, isModuleEnabled],
  );

  const visibleNavGroups = useMemo(
    () => {
      if (!navReady) return [];
      return navGroups
        .map((group) => ({ ...group, items: group.items.filter(isItemVisible) }))
        .filter((group) => group.items.length > 0);
    },
    [navReady, isItemVisible],
  );

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const sidebarWidth = collapsed ? 'w-60 lg:w-16' : 'w-60';

  return (
    <div className="flex h-full">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden print:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        id="primary-navigation"
        className={`fixed inset-y-0 z-40 flex flex-col bg-gray-900 text-white transition-all duration-200 print:hidden
          start-0
          ${sidebarWidth}
          ${mobileOpen
            ? 'translate-x-0'
            : 'ltr:-translate-x-full rtl:translate-x-full lg:ltr:translate-x-0 lg:rtl:translate-x-0'
          }
        `}
      >
        {/* Logo */}
        <div className="flex h-14 items-center gap-2 px-4 border-b border-gray-800 shrink-0">
          <img src="/images/icon_48x48.png" alt="Dada" className="w-7 h-7 rounded" />
          <span className={`font-semibold text-sm whitespace-nowrap ${collapsed ? 'lg:hidden' : ''}`}>
            {t('common.appName')}
          </span>
        </div>

        {/* Nav links */}
        <nav className="flex-1 overflow-y-auto py-3 scrollbar-thin">
          {!navReady ? (
            // Skeleton placeholders so the sidebar doesn't appear empty while loading
            Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-2.5">
                <div className="w-5 h-5 rounded bg-gray-700 animate-pulse shrink-0" />
                <div className={`h-4 w-28 rounded bg-gray-700 animate-pulse ${collapsed ? 'lg:hidden' : ''}`} />
              </div>
            ))
          ) : (
            visibleNavGroups.map((group, groupIndex) => (
              <div key={group.labelKey ?? `group-${groupIndex}`} className={groupIndex === 0 ? '' : 'mt-3'}>
                {group.labelKey && (
                  <div className={`px-4 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 ${collapsed ? 'lg:hidden' : ''}`}>
                    {t(group.labelKey)}
                  </div>
                )}
                {group.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={() => {
                        setMobileOpen(false);
                      }}
                      title={collapsed ? t(item.labelKey) : undefined}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                          isActive
                            ? 'bg-primary-600/30 text-primary-300 border-primary-400 ltr:border-l-[3px] rtl:border-r-[3px]'
                            : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                        }`
                      }
                    >
                      <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                      <span className={`truncate ${collapsed ? 'lg:hidden' : ''}`}>{t(item.labelKey)}</span>
                    </NavLink>
                  );
                })}
              </div>
            ))
          )}
        </nav>

        {navReady && (
          <div className="border-t border-gray-800 py-2">
            <NavLink
              to={settingsNavItem.to}
              onClick={() => {
                setMobileOpen(false);
              }}
              title={collapsed ? t(settingsNavItem.labelKey) : undefined}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                  isActive
                    ? 'bg-primary-600/30 text-primary-300 border-primary-400 ltr:border-l-[3px] rtl:border-r-[3px]'
                    : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                }`
              }
            >
              <Settings className="h-5 w-5 shrink-0" aria-hidden="true" />
              <span className={`truncate ${collapsed ? 'lg:hidden' : ''}`}>{t(settingsNavItem.labelKey)}</span>
            </NavLink>
          </div>
        )}

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed((value) => !value)}
          className="hidden lg:flex items-center justify-center h-10 border-t border-gray-800 text-gray-500 hover:text-white transition-colors"
          aria-label={t(collapsed ? 'nav.expandSidebar' : 'nav.collapseSidebar')}
          aria-controls="primary-navigation"
          aria-expanded={!collapsed}
        >
          {collapsed
            ? <ChevronRight className="h-4 w-4 rtl:rotate-180" aria-hidden="true" />
            : <ChevronLeft className="h-4 w-4 rtl:rotate-180" aria-hidden="true" />}
        </button>
      </aside>

      {/* Main wrapper */}
      <div
        className={`flex flex-1 flex-col min-h-0 transition-all duration-200 print:ml-0 print:mr-0 print:min-h-full
          ltr:lg:ml-16 rtl:lg:mr-16
          ${!collapsed ? 'ltr:lg:ml-60 rtl:lg:mr-60' : ''}
        `}
      >
        {/* Top bar */}
        <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-gray-200 bg-white px-4 shrink-0 print:hidden">
          {/* Mobile hamburger */}
          <button
            className="lg:hidden text-gray-600 hover:text-gray-900"
            onClick={() => setMobileOpen(true)}
            aria-label={t('nav.openMenu')}
            aria-controls="primary-navigation"
            aria-expanded={mobileOpen}
          >
            <Menu className="h-6 w-6" aria-hidden="true" />
          </button>

          {/* Spacer */}
          <div className="flex-1" />

          <div className="flex items-center gap-3 ltr:ml-auto rtl:mr-auto">
            <WorkspaceSwitcher />
            <LanguageSwitcher />

            {/* User info */}
            {user && (
              <div className="flex items-center gap-2">
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-7 h-7 rounded-full"
                  />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-semibold">
                    {user.name?.charAt(0)?.toUpperCase()}
                  </div>
                )}
                <span className="hidden sm:inline text-sm text-gray-700 font-medium">
                  {user.name}
                </span>
              </div>
            )}

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="text-sm text-gray-500 hover:text-red-600 transition-colors"
              aria-label="Logout"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </header>

        {/* Main content */}
        <main
          className="flex-1 overflow-y-auto scrollbar-thin bg-gray-50 p-4 md:p-6 print:overflow-visible print:h-auto print:w-full print:bg-white print:p-0"
        >
          <Outlet />
        </main>
      </div>



    </div>
  );
}
